import { getFormattedDate } from '../../utils/utilities';
import clsx from 'clsx';
import { WhisperComponent } from '../developers/page-components/WhisperComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import { FakeEditor } from './FakeEditor';
import { OverflowTooltip } from '../OverflowTooltip';
import { numberFormatter, valueFormatter } from '../../utils/utilities';

export interface IFakeInputProps {
    label?: string;
    value?: string | number | Date;
    type?: string;
    defaultValue?: string | number | Date;
    additionalClasses?: string;
    fakeInputAdditionalClass?: string;
    fakeInputLabelAdditionalClasses?: string;
    tooltipText?: string;
    isLabelIcon?: boolean;
    link?: string;
    isValueTooltip?: boolean;
    linkAdditionalClassName?: string;
    suffix?: string;
    prefix?: string; // New property for prefix
    autoComplete?: string;
    useNumberFormatter?: boolean;
}

export const FakeInput = (props: IFakeInputProps): JSX.Element => {
    const {
        label, value, type, defaultValue,
        additionalClasses, fakeInputAdditionalClass, fakeInputLabelAdditionalClasses,
        tooltipText, isLabelIcon = false, link, isValueTooltip = false,
        linkAdditionalClassName, suffix, prefix, autoComplete, useNumberFormatter = false
    } = props;

    let showValue;
    let tooltipValue;

    if (type === 'date') {
        showValue = getFormattedDate(value as Date, 'd MMM, yyyy', defaultValue);
    } else if (type === 'number' && useNumberFormatter && value !== null && value !== undefined) {
        // Special formatting for numbers when useNumberFormatter is true
        try {
            const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
            if (!isNaN(numValue)) {
                showValue = valueFormatter(numValue);
                tooltipValue = numberFormatter(numValue);
            } else {
                showValue = value;
                tooltipValue = value;
            }
        } catch (e) {
            // If there's an error parsing the number, just use the original value
            showValue = value;
            tooltipValue = value;
        }
    } else {
        showValue = value;
    }

    const textValue = showValue ?? defaultValue ?? '-';
    const hasValue = textValue !== '-' && textValue !== null && textValue !== undefined && textValue !== '';

    // Only show prefix and suffix if there's a value
    const displayPrefix = hasValue && prefix ? prefix : '';
    const displaySuffix = hasValue && suffix ? suffix : '';

    const displayValue = useNumberFormatter && type === 'number' && hasValue ?
        (
            <WhisperComponent
                isTooltip
                whisperText={`${displayPrefix}${tooltipValue}${displaySuffix ? ' ' + displaySuffix : ''}`}
                mainText={`${displayPrefix}${textValue}${displaySuffix ? ' ' + displaySuffix : ''}`}
                additionalClass="cursor-help"
            />
        ) : (
            `${displayPrefix}${textValue}${displaySuffix ? ' ' + displaySuffix : ''}`
        );

    const linkText = link
        ? <a href={link} target="_blank" rel="noreferrer" className={clsx('d-block mb-3 mt-1 py-2 fs-6', linkAdditionalClassName)}>{link}</a>
        : (
            <div className="mb-3 mt-1 py-2 fs-6">
                {useNumberFormatter && type === 'number' && hasValue ?
                    displayValue :
                    <OverflowTooltip text={`${displayPrefix}${textValue}${displaySuffix ? ' ' + displaySuffix : ''}`} />
                }
            </div>
        );

    switch (type) {
        case 'description':
            return (
                <div className={clsx('fv-row input-container mb-7', additionalClasses)}>
                    <div className="form-label  col-12">
                        <span className="text-muted fs-6">{label}</span>
                        <div className="mb-3 mt-1 fs-6">
                            {`${showValue ?? defaultValue ?? '-'}`}
                        </div>
                    </div>
                </div>
            );
        case 'editor':
            return (
                <FakeEditor
                    value={value}
                    label={label}
                />
            );
        default: {
            return (
                <div className={clsx('col-6 col-sm-4 col-md-3 fv-row input-container mb-3', fakeInputAdditionalClass)}>
                    <div className="form-label col-12">
                        <span className={clsx('text-muted fs-6', fakeInputLabelAdditionalClasses)}>{label}</span>
                        {
                            isLabelIcon && (
                                <WhisperComponent
                                    isTooltip
                                    whisperText={tooltipText}
                                    mainText={
                                        <FontAwesomeIcon
                                            icon={faQuestionCircle}
                                            className="tooltip-icon cursor-pointer ms-1 text-muted"
                                            width={12}
                                        />
                                    }
                                />
                            )
                        }

                    </div>
                    {
                        isValueTooltip
                            ? (
                                <WhisperComponent
                                    isTooltip
                                    whisperText={tooltipText}
                                    mainText={(
                                        <span className="mb-3 mt-2 fs-6 text-overflow">
											{`${textValue}`}
										</span>
                                    )}
                                />
                            ) : linkText
                    }
                </div>
            );
        }

    }
};
